import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { MetricWithEntries, MetricCategory, DateColumn } from '../../types'
import MetricRow from './MetricRow'

interface Props {
  metrics: MetricWithEntries[]
  categories: MetricCategory[]
  dateColumns: DateColumn[]
  teamId: number
  groupBy: string
  viewMode?: 'numbers' | 'wonlost' | 'both'
  savingCells: Set<string>
  /** Ids of the metrics whose inline target edit is in flight. */
  savingTargets: Set<number>
  onTargetSave: (metric: MetricWithEntries, raw: string) => void
  onValueChange: (metric: MetricWithEntries, column: DateColumn, rawValue: string) => void
  onValueEnter: (metricId: number, columnKey: string, el: HTMLInputElement) => void
  onValueBlur: (metricId: number, columnKey: string) => void
  registerValueRef: (metricId: number, columnKey: string, el: HTMLInputElement | null) => void
  onWonLostToggle: (metric: MetricWithEntries, column: DateColumn, newValue: boolean) => void
  onEditNotes: (metric: MetricWithEntries) => void
  onEdit: (metric: MetricWithEntries) => void
  onArchive: (metric: MetricWithEntries) => void
  onDelete: (metric: MetricWithEntries) => void
  onReorder?: (sourceIndex: number, destinationIndex: number) => void
}

export default function ScoreboardTable({ metrics, categories, dateColumns, teamId, groupBy, viewMode = 'both', savingCells, savingTargets, onTargetSave, onValueChange, onValueEnter, onValueBlur, registerValueRef, onWonLostToggle, onEditNotes, onEdit, onArchive, onDelete, onReorder }: Props) {
  // Shared by every render path (grouped, flat, draggable) so a row behaves
  // identically however the table is grouped.
  const cellProps = {
    dateColumns,
    teamId,
    savingCells,
    onTargetSave,
    onValueChange,
    onValueEnter,
    onValueBlur,
    registerValueRef,
    onWonLostToggle,
    onEditNotes,
    onEdit,
    onArchive,
    onDelete,
    viewMode,
  }

  const renderStaticRow = (metric: MetricWithEntries) => (
    <MetricRow
      key={metric.id}
      metric={metric}
      savingTarget={savingTargets.has(metric.id)}
      {...cellProps}
    />
  )

  const renderRows = () => {
    if (groupBy === 'category') {
      const categorized = categories.map(cat => ({
        category: cat,
        items: metrics.filter(m => m.category_id === cat.id),
      }))
      const uncategorized = metrics.filter(m => !m.category_id)

      return (
        <>
          {categorized.map(group => group.items.length > 0 && (
            <tbody key={`cat-${group.category.id}`}>
              <tr className="bg-charcoal/5">
                <td colSpan={5 + dateColumns.length + 1} className="py-2 px-3 text-sm font-semibold text-text-primary uppercase tracking-wider">
                  {group.category.name}
                </td>
              </tr>
              {group.items.map(renderStaticRow)}
            </tbody>
          ))}
          {uncategorized.length > 0 && (
            <tbody>
              <tr className="bg-charcoal/5">
                <td colSpan={5 + dateColumns.length + 1} className="py-2 px-3 text-sm font-semibold text-text-primary uppercase tracking-wider">
                  Uncategorized
                </td>
              </tr>
              {uncategorized.map(renderStaticRow)}
            </tbody>
          )}
        </>
      )
    }

    if (groupBy === 'owner') {
      const ownerMap: Record<string, { owner: MetricWithEntries['owner']; items: MetricWithEntries[] }> = {}
      metrics.forEach(m => {
        const key = m.owner ? `${m.owner.id}` : 'unassigned'
        if (!ownerMap[key]) {
          ownerMap[key] = { owner: m.owner, items: [] }
        }
        ownerMap[key].items.push(m)
      })

      return (
        <>
          {Object.entries(ownerMap).map(([key, group]) => (
            <tbody key={`owner-${key}`}>
              <tr className="bg-charcoal/5">
                <td colSpan={5 + dateColumns.length + 1} className="py-2 px-3 text-sm font-semibold text-text-primary uppercase tracking-wider">
                  {group.owner ? group.owner.full_name : 'Unassigned'}
                </td>
              </tr>
              {group.items.map(renderStaticRow)}
            </tbody>
          ))}
        </>
      )
    }

    if (!onReorder) {
      return (
        <tbody>
          {metrics.map(renderStaticRow)}
        </tbody>
      )
    }

    const handleDragEnd = (result: DropResult) => {
      if (!result.destination) return
      if (result.destination.index === result.source.index) return
      onReorder(result.source.index, result.destination.index)
    }

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="metrics" type="metric">
          {(provided) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {metrics.map((metric, index) => (
                <Draggable key={metric.id} draggableId={String(metric.id)} index={index}>
                  {(prov, snapshot) => (
                    <MetricRow
                      ref={prov.innerRef}
                      metric={metric}
                      savingTarget={savingTargets.has(metric.id)}
                      {...cellProps}
                      dragHandleProps={prov.dragHandleProps}
                      isDragging={snapshot.isDragging}
                      rowProps={prov.draggableProps}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      </DragDropContext>
    )
  }

  if (metrics.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-card border border-stone/10 p-12 text-center">
        <svg className="w-12 h-12 text-text-primary/20 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h3 className="text-lg font-semibold text-text-primary mb-1">No metrics yet</h3>
        <p className="text-sm text-text-secondary">Add a metric to start tracking your team's scoreboard.</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-card border border-stone/10 overflow-hidden">
      {/* max-h + overflow-auto makes this the scroll container for BOTH
          axes so the header row can stick to the top while the left cells
          keep sticking horizontally. Corner header cells pin on both axes
          (top + left/right) with a higher z-index than the row cells. */}
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-charcoal text-white">
              <th className="py-3 px-2 text-left text-sm font-semibold uppercase tracking-wider sticky top-0 left-0 bg-charcoal z-40 w-[36px] min-w-[36px] max-w-[36px]"></th>
              <th className="py-3 px-3 text-left text-sm font-semibold uppercase tracking-wider sticky top-0 left-[36px] bg-charcoal z-40 w-[180px] min-w-[180px] max-w-[180px]">Metric</th>
              <th className="py-3 px-3 text-left text-sm font-semibold uppercase tracking-wider sticky top-0 left-[216px] bg-charcoal z-40 w-[140px] min-w-[140px] max-w-[140px]">Owner</th>
              <th className="py-3 px-3 text-left text-sm font-semibold uppercase tracking-wider sticky top-0 left-[356px] bg-charcoal z-40 w-[140px] min-w-[140px] max-w-[140px]">Target</th>
              <th className="py-3 px-3 text-left text-sm font-semibold uppercase tracking-wider sticky top-0 left-[496px] bg-charcoal z-40 w-[130px] min-w-[130px] max-w-[130px]" style={{ boxShadow: '4px 0 8px -2px rgba(0,0,0,0.15)' }}>YTD</th>
              {dateColumns.map(col => (
                <th
                  key={col.key}
                  className={`py-3 px-3 text-center text-sm font-semibold uppercase tracking-wider sticky top-0 z-30 min-w-[110px] ${col.is_current ? 'bg-gray-600' : 'bg-charcoal'}`}
                >
                  {col.label}
                </th>
              ))}
              <th className="py-3 px-2 text-center text-sm font-semibold uppercase tracking-wider sticky top-0 right-0 bg-charcoal z-40 w-[40px] min-w-[40px] max-w-[40px]" style={{ boxShadow: '-4px 0 8px -2px rgba(0,0,0,0.15)' }}></th>
            </tr>
          </thead>
          {renderRows()}
        </table>
      </div>
    </div>
  )
}
